import React, {useEffect, useState} from 'react';
import {getDetailedReactionsRequest} from 'util/api';
import {centsToString} from 'util/cashFormatter';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import 'styles/pictures/detail/ReactionListPopup.css';
import UniversalTypeFilter from 'components/universal/UniversalTypeFilter';
import UniversalProfileImage from 'components/universal/UniversalProfileImage';
import {useNavigate} from 'react-router-dom';

const ReactionListPopup = ({comment}) => {
    const navigate = useNavigate();
    const [detailedReactions, setDetailedReactions] = useState([]);
    const [selectedType, setSelectedType] = useState('ALL');
    const [upperList, setUpperList] = useState([]);
    const [groupedReactions, setGroupedReactions] = useState({});
    const [shownReactions, setShownReactions] = useState([]);

    useEffect(() => {
        getDetailedReactionsRequest(comment.id).then(
            (reactions) => {
                setDetailedReactions(reactions);
            },
            (error) => {
                console.error('Failed to get detailed reactions: ', error);
            }
        );
    }, [comment.id]);

    useEffect(() => {
        let localShownReactions;
        if (selectedType === 'ALL') {
            localShownReactions = detailedReactions;
        } else {
            localShownReactions = detailedReactions.filter((reaction) => reaction.reactionType === selectedType);
        }
        localShownReactions.sort((a, b) => b.reactedOn - a.reactedOn);
        setShownReactions(localShownReactions);
    }, [detailedReactions, groupedReactions, selectedType]);

    const optionClicked = (option) => {
        setSelectedType(option);
    };

    const openProfile = (authorId) => {
        if (!authorId) {
            return;
        }
        navigate(`/profile/${authorId}`);
    };

    const groupByType = (reactions) => {
        return reactions.reduce((acc, reaction) => {
            const type = reaction.reactionType;
            if (!acc[type]) {
                acc[type] = {
                    reactions: [],
                    totalSum: 0,
                    latestTime: 0,
                };
            }
            acc[type].reactions.push(reaction);
            acc[type].totalSum += reaction.price;
            acc[type].latestTime = Math.max(acc[type].latestTime, reaction.reactedOn);
            return acc;
        }, {});
    };

    function transformReactions(input) {
        return Object.entries(input).flatMap(([itemType, data]) =>
            data.reactions.map(reaction => ({
                ...reaction,
                itemType,
                totalSum: data.totalSum,
                latestTime: data.latestTime
            }))
        );
    }

    useEffect(() => {
        let localGroupedReactions = groupByType(detailedReactions);
        localGroupedReactions = transformReactions(localGroupedReactions);
        localGroupedReactions.sort((a, b) => {
            if (b.totalSum !== a.totalSum) {
                return b.totalSum - a.totalSum;
            }
            return b.latestTime - a.latestTime;
        });

        let myFormat = Object.entries(
            localGroupedReactions.reduce((acc, reaction) => {
                acc[reaction.reactionType] = `${itemTypeToEmoji[reaction.reactionType].emoji} (${centsToString(reaction.totalSum)})`;
                return acc;
            }, {})
        );

        const localAllReactionsTotalPrice = detailedReactions.reduce((total, reaction) => total + reaction.price, 0);
        myFormat.unshift(['ALL', `All (${centsToString(localAllReactionsTotalPrice)})`]);
        setUpperList(myFormat);
        setGroupedReactions(localGroupedReactions);
    }, [detailedReactions]);

    return (
        <div className="reaction-list-pane">
            <div style={{marginRight: '40px'}} className="notification-type-filter">
                <UniversalTypeFilter itemClicked={optionClicked} types={upperList} highlightedKeys={[selectedType]}/>
            </div>
            <table className="reactions-table">
                <tbody>
                {shownReactions.map((reaction, index) => (
                    <tr
                        key={`${reaction.username}-${reaction.reactionType}-${index}`}
                        className="reaction-row"
                        onClick={() => openProfile(reaction.authorId)}
                    >
                        <td className="reaction-user-cell">
                            <div className="reaction-user-info">
                                <UniversalProfileImage
                                    id={reaction.authorProfilePictureId}
                                    size="32px"
                                    cssClass="reaction-user-image"
                                />
                                <span className="reaction-username">{reaction.username}</span>
                            </div>
                        </td>
                        <td>{itemTypeToEmoji[reaction.reactionType]?.emoji || ''}</td>
                        <td>{centsToString(reaction.price)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default ReactionListPopup;
